import React, { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { queryClient } from '@/App';
import { SuricattaTrainingFormSchema } from '@/constants/formSchema/SuricattaTrainingFormSchema';
import api from '@/services/apiSgft';
import { SuricattaTrainingData } from '@/types/SuricattaTrainingData';

import { Button } from '../atoms/Button';
import Modal from '../atoms/Modal';
import UncontrolledInputText from '../atoms/UncontrolledInputText';
import ExpandableSubtitle from './ExpandableSubtitle';
import { SuricattaTrainingSection } from './SuricattaTrainingSection';

interface SuricattaTrainingModalProps {
	isOpen: boolean;
	onClose: () => void;
	type: 'create' | 'edit';
	suricattaTraining?: SuricattaTrainingData;
}

export const SuricattaTrainingModal: React.FC<SuricattaTrainingModalProps> = ({
	isOpen,
	onClose,
	type,
	suricattaTraining,
}) => {
	const [isSubmiting, setIsSubmiting] = useState(false);

	const form = useForm<z.infer<typeof SuricattaTrainingFormSchema>>({
		defaultValues: {
			id: type === 'edit' ? suricattaTraining?.id : null,
			name: type === 'edit' ? suricattaTraining?.name : '',
			code: type === 'edit' ? suricattaTraining?.code : '',
			sgftTrainings: suricattaTraining?.sgftTrainings.map((sgftTraining) => ({
				trainingType: sgftTraining.training.trainingType,
				id: sgftTraining.training.id,
			})) ?? [{ trainingType: '', id: null }],
		},
	});

	const control = form.control;

	const closeAndClear = () => {
		form.reset();
		onClose();
	};

	const onSubmit = async (
		data: z.infer<typeof SuricattaTrainingFormSchema>,
	) => {
		try {
			setIsSubmiting(true);
			const submitData = {
				name: data?.name.toUpperCase(),
				code: data?.code,
				sgftTrainings: data.sgftTrainings.map(
					(sgftTraining) => sgftTraining.id,
				),
			};
			if (type === 'create') {
				await api.post('/suricatta-training/', submitData);
			} else {
				await api.put(
					`/suricatta-training/${suricattaTraining?.id}`,
					submitData,
				);
			}
			toast.success(
				`Treinamento! ${type === 'create' ? 'criado' : 'salvo'} com sucesso!`,
				{
					theme: 'colored',
					toastId: 'success',
				},
			);
			queryClient.invalidateQueries(['get-all-suricatta-trainings-data']);
		} catch (error: any) {
			const errorMessage =
				error?.response?.status < 500
					? error?.response?.data?.message
					: `Erro ao ${type === 'create' ? 'criar' : 'salvar'} treinamento!`;
			toast.error(errorMessage, {
				theme: 'colored',
				toastId: 'error',
			});
		}
		setIsSubmiting(false);
		closeAndClear();
	};

	const {
		fields: sgftTrainingFields,
		append: appendSgftTraining,
		remove: removeSgftTraining,
	} = useFieldArray<z.infer<typeof SuricattaTrainingFormSchema>>({
		control,
		name: 'sgftTrainings',
	});

	return (
		<Modal
			isOpen={isOpen}
			onClose={closeAndClear}
			title={`${type === 'create' ? 'Cadastro de Treinamento Suricatta' : 'Edição de Treinamento Suricatta'}`}
			className="z-[100] w-[50rem] max-w-[85vw]"
		>
			<FormProvider {...form}>
				<form
					className="space-between flex h-[90%] flex-col px-5 py-2"
					onSubmit={form.handleSubmit(onSubmit)}
				>
					<div className="flex w-[47rem] flex-col items-center">
						<div className="flex w-full justify-between">
							<UncontrolledInputText
								title="Nome"
								required
								width="37rem"
								{...form.register('name')}
								upperCase
								disabled
							/>
							<UncontrolledInputText
								title="Código"
								required
								width="9rem"
								{...form.register('code')}
								upperCase
								disabled
							/>
						</div>
						<div>
							<ExpandableSubtitle
								subtitle="Treinamentos Equivalentes"
								containerClassName="w-[48rem]"
							>
								<div className="flex w-full flex-col gap-2">
									<div className="my-3 flex flex-col gap-1">
										<div className="flex flex-col gap-2">
											{sgftTrainingFields.map((field, index) => (
												<SuricattaTrainingSection
													key={field.id}
													index={index}
													form={form}
													remove={() => removeSgftTraining(index)}
												/>
											))}
										</div>
										<div className="w-full items-start px-4">
											<button
												className="text-xs font-medium text-primary hover:opacity-80"
												onClick={() =>
													appendSgftTraining({ trainingType: '', id: null })
												}
												type="button"
											>
												+ Adicionar Treinamento Equivalente
											</button>
										</div>
									</div>
								</div>
							</ExpandableSubtitle>
						</div>

						<div className="my-2 mt-[1.5rem] flex w-full justify-between">
							<Button
								type="button"
								className="h-8 min-w-36 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1]"
								onClick={closeAndClear}
							>
								Cancelar
							</Button>
							<Button
								type="submit"
								className="h-8 min-w-36"
								isLoading={isSubmiting}
							>
								Salvar
							</Button>
						</div>
					</div>
				</form>
			</FormProvider>
		</Modal>
	);
};
